import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useParams, useLocation } from 'react-router-dom';
import AppHeader from '../components/Header';
import FilterSet from '../components/FilterSet';
import ImageFilterSet from '../components/ImageFilterSet';
import PageHeader from '../components/PageHeader';
import Chatbot from '../components/Chatbot';
import { formatDate } from '../components/FilterSet';
import { useFilterContext } from '../context/FilterContext';
import { ChatContextProvider } from '../context/ChatContext';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import { useThemeContext } from '../App';
import { CircularProgress } from '@mui/material';
import { useOrganizationMetadata } from '../hooks/useBrands';

const MainLayout: React.FC = () => {
    const { orgId: orgIdStr } = useParams<{ orgId: string }>();
    const orgId = parseInt(orgIdStr || '0', 10);
    const { metadata } = useOrganizationMetadata(orgId);
    const { 
        useDefaultFilters, 
        startDate, 
        endDate, 
        selectedBrands, 
        selectedPlatforms, 
        selectedPillars, 
        isInitialized,
        dashboardType,
        orgName
    } = useFilterContext();
    const { mode, toggleTheme } = useThemeContext();
    const location = useLocation();
    
    const [imageStartDate, setImageStartDate] = useState<Date | null>(null);
    const [imageEndDate, setImageEndDate] = useState<Date | null>(null);
    
    const isImageComparisonPage = location.pathname.includes('/image-comparison');
    
    useEffect(() => {
        const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const prefix = isDev ? 'DEV ' : '';
        const orgName = metadata?.organization_name || 'Loading...';
        document.title = `${prefix}Inklantern - ${orgName}`;
    }, [metadata]);

    // Condense the sticky page header once the page has scrolled a bit. The
    // two thresholds keep it from flickering when the header changes height.
    const [condensed, setCondensed] = useState(false);
    const handleMainScroll = useCallback((event: React.UIEvent<HTMLElement>) => {
        const top = event.currentTarget.scrollTop;
        setCondensed(prev => (prev ? top > 12 : top > 56));
    }, []);

    const handleImageDatesChange = (newStartDate: Date | null, newEndDate: Date | null) => {
        setImageStartDate(newStartDate);
        setImageEndDate(newEndDate);
    };

    const formattedStartDate = formatDate(startDate);
    const formattedEndDate = formatDate(endDate);

    const outletContextValue = {
        selectedBrands,
        selectedPlatforms,
        selectedPillars,
        startDate: formattedStartDate,
        endDate: formattedEndDate
    };

    const imageOutletContextValue = {
        ...outletContextValue,
        imageStartDate,
        imageEndDate
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            <AppHeader 
                isDarkMode={mode === 'dark'}
                onToggleTheme={toggleTheme}
            />
            
            <Box
                component="main"
                onScroll={handleMainScroll}
                sx={{ flexGrow: 1, overflow: 'auto', bgcolor: 'background.default' }}
            >
                {!isInitialized ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 150px)' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {/* Space above the page header; scrolls away before the header sticks */}
                        <Box sx={{ height: { xs: 12, md: 16 } }} />

                        {/* Org name + filters. On wide screens this row sticks under the
                            nav bar and condenses once the page scrolls. */}
                        <Box
                            sx={(theme) => ({
                                position: { xs: 'static', lg: 'sticky' },
                                top: 0,
                                zIndex: theme.zIndex.appBar - 1,
                                bgcolor: condensed ? alpha(theme.palette.background.default, 0.9) : 'background.default',
                                backdropFilter: condensed ? 'saturate(1.4) blur(10px)' : 'none',
                                borderBottom: '1px solid',
                                borderColor: condensed ? 'divider' : 'transparent',
                                boxShadow: condensed
                                    ? `0 8px 18px -14px ${alpha('#1B1526', theme.palette.mode === 'dark' ? 0.9 : 0.45)}`
                                    : 'none',
                                transition: 'border-color 150ms ease, box-shadow 150ms ease, background-color 150ms ease',
                            })}
                        >
                            <Box
                                sx={{
                                    maxWidth: 1600,
                                    mx: 'auto',
                                    px: { xs: 2, md: 3, lg: 4 },
                                    py: { xs: 1, lg: condensed ? 1.25 : 1.5 },
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: { xs: 'stretch', lg: condensed ? 'center' : 'flex-start' },
                                    flexDirection: { xs: 'column', lg: 'row' },
                                    gap: { xs: 2, lg: 4 },
                                }}
                            >
                                <Box sx={{ flex: '0 1 auto', minWidth: 0 }}>
                                    {orgIdStr && <PageHeader condensed={condensed} />}
                                </Box>

                                {isImageComparisonPage ? (
                                    <Box sx={{ flex: '1 1 auto', maxWidth: { lg: 900 } }}>
                                        <ImageFilterSet onDatesChange={handleImageDatesChange} />
                                    </Box>
                                ) : (
                                    useDefaultFilters && (
                                        <Box
                                            sx={{
                                                flex: '1 1 auto',
                                                maxWidth: { lg: dashboardType === 'personal_branding' ? 720 : 900 },
                                            }}
                                        >
                                            <FilterSet />
                                        </Box>
                                    )
                                )}
                            </Box>
                        </Box>

                        <Box sx={{ maxWidth: 1600, mx: 'auto', px: { xs: 2, md: 3, lg: 4 }, pt: { xs: 2, md: 2.5 }, pb: 8 }}>
                            <Outlet context={isImageComparisonPage ? imageOutletContextValue : outletContextValue} />
                        </Box>
                    </>
                )}
            </Box>

            {/* AI Chatbot - wrapped in ChatContextProvider for page/filter awareness */}
            {isInitialized && orgId > 0 && (
                <ChatContextProvider>
                    <Chatbot orgId={orgId} orgName={orgName || undefined} />
                </ChatContextProvider>
            )}
        </Box>
    );
};

export default MainLayout;